import { Link } from 'react-router-dom';
import { useStateContext } from '../../hooks/useStateContext';
import useSEO from '../../hooks/useSEO';
import { schemaGraph } from '../../utils/schema';
import { GUIDE_INDEX as ARIZONA_GUIDES } from '../resources/ResourcesHubPage';
import { KANSAS_HUB } from '../resources/kansasHubContent';

// Per-state guide indexes. As we write guides for new states, add them here.
// Note: Arizona keeps its existing flat guide URLs (/arizona-real-estate-ce-requirements etc.)
// to preserve their existing Google rankings; we just link to them from this hub.
const GUIDES_BY_STATE = {
  arizona: ARIZONA_GUIDES,
  // nevada, etc. — empty for now
};

// State share images (1200x630, public/og/). States without one use the sitewide default.
const SHARE_IMAGE_BY_STATE = {
  arizona: 'https://steadyeducation.com/og/arizona.png',
  kansas: 'https://steadyeducation.com/og/kansas.png',
};

// States with a full hub (quick facts, FAQ, sources) rather than just a guide grid.
const HUB_BY_STATE = {
  kansas: KANSAS_HUB,
};

// CollectionPage + ItemList of guides + FAQPage for states with a full hub.
function hubSchemas(hub, pageUrl) {
  return [
    {
      '@type': 'CollectionPage',
      '@id': `${pageUrl}#webpage`,
      url: pageUrl,
      name: hub.h1,
      description: hub.seoDescription,
      inLanguage: 'en-US',
      isPartOf: { '@id': 'https://steadyeducation.com/#website' },
      publisher: { '@id': 'https://steadyeducation.com/#organization' },
      dateModified: hub.reviewedIso,
      mainEntity: { '@id': `${pageUrl}#guides` },
    },
    {
      '@type': 'ItemList',
      '@id': `${pageUrl}#guides`,
      itemListElement: hub.guides.map((g, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        url: `https://steadyeducation.com${g.url}`,
        name: g.title,
      })),
    },
    {
      '@type': 'FAQPage',
      '@id': `${pageUrl}#faq`,
      mainEntity: hub.faq.map(f => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
  ];
}

export default function StateResourcesHubPage() {
  const { state } = useStateContext();
  const hub = HUB_BY_STATE[state.slug];
  const pageUrl = `https://steadyeducation.com/${state.slug}/resources`;

  useSEO({
    title: hub ? hub.seoTitle : `${state.name} Real Estate CE Resources | Steady Education`,
    description: hub ? hub.seoDescription : `Plain-English guides on ${state.name} real estate CE: requirements, rules, renewal, and more. ${state.regulator}-verified.`,
    url: pageUrl,
    image: SHARE_IMAGE_BY_STATE[state.slug],
    imageAlt: SHARE_IMAGE_BY_STATE[state.slug] ? `${state.name} real estate CE guides from Steady Education` : undefined,
    jsonLd: schemaGraph({
      mainSchemas: hub ? hubSchemas(hub, pageUrl) : [],
      breadcrumb: [
        { name: 'Home', url: 'https://steadyeducation.com' },
        { name: state.name, url: `https://steadyeducation.com/${state.slug}` },
        { name: 'Resources', url: pageUrl },
      ],
    }),
  });

  if (hub) return <FullHub state={state} hub={hub} />;

  const guides = GUIDES_BY_STATE[state.slug] || [];

  return (
    <div style={styles.page}>
      <div style={styles.hero}>
        <div className="container">
          <h1 style={styles.heroTitle}>{state.name} Real Estate CE Resources</h1>
          <p style={styles.heroSubtitle}>
            Plain-English guides on {state.name}'s CE requirements — written for licensees,
            verified against the {state.regulator}.
          </p>
        </div>
      </div>

      <div className="container" style={styles.content}>
        {guides.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>📚</div>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 22, color: 'var(--slate-900)', marginBottom: 8 }}>
              {state.name} guides launching soon
            </h2>
            <p style={{ color: 'var(--color-text-muted)', maxWidth: 480, margin: '0 auto 20px', lineHeight: 1.6 }}>
              We're writing plain-English guides for {state.name} licensees. In the meantime, the fastest
              way to satisfy your CE is the complete package.
            </p>
            <Link to={`/${state.slug}/courses`} className="btn btn-primary">See {state.name} Courses →</Link>
          </div>
        ) : (
          <>
            <div style={styles.grid}>
              {guides.map(g => (
                <Link key={g.url} to={g.url} style={styles.card}>
                  <div style={styles.cardLabel}>{g.label}</div>
                  <div style={styles.cardTitle}>{g.title}</div>
                  <div style={styles.cardDesc}>{g.desc}</div>
                  <div style={styles.cardArrow}>Read guide →</div>
                </Link>
              ))}
            </div>

            <div style={styles.ctaSection}>
              <div style={styles.ctaInner}>
                <div>
                  <div style={styles.ctaLabel}>Ready to renew?</div>
                  <div style={styles.ctaTitle}>Complete {state.name} CE Package — ${state.packagePrice}</div>
                  <div style={styles.ctaSub}>All {state.regulator}-required hours. Self-paced, instant certificates.</div>
                </div>
                <Link to={`/${state.slug}/courses`} style={styles.ctaBtn}>See the package →</Link>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Full hub: hero, quick facts, guide grid, FAQ, official sources, CTA.
// All text is rendered (no click-to-load) so it lands in the prerendered HTML.
function FullHub({ state, hub }) {
  return (
    <div style={styles.page}>
      <div style={styles.hero}>
        <div className="container">
          <nav aria-label="Breadcrumb" style={styles.crumbs}>
            <Link to="/" style={styles.crumbLink}>Home</Link> ›{' '}
            <Link to={`/${state.slug}`} style={styles.crumbLink}>{state.name}</Link> › Resources
          </nav>
          <h1 style={styles.heroTitle}>{hub.h1}</h1>
          <p style={styles.heroSubtitle}>{hub.intro}</p>
          <p style={styles.reviewed}>
            <time dateTime={hub.reviewedIso}>{hub.reviewedLabel}</time> · Verified against {state.regulator}
          </p>
        </div>
      </div>

      <div className="container" style={styles.content}>
        <section style={styles.section}>
          <h2 style={styles.h2}>{state.name} CE at a glance</h2>
          <dl style={styles.facts}>
            {hub.facts.map(f => (
              <div key={f.term} style={styles.factRow}>
                <dt style={styles.factTerm}>{f.term}</dt>
                <dd style={styles.factValue}>{f.value}</dd>
              </div>
            ))}
          </dl>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>{state.name} CE guides</h2>
          <div style={{ ...styles.grid, marginBottom: 0 }}>
            {hub.guides.map(g => (
              <article key={g.url} style={styles.card}>
                <div style={styles.cardLabel}>{g.label}</div>
                <h3 style={{ ...styles.cardTitle, margin: '4px 0 0' }}>
                  <Link to={g.url} style={styles.cardTitleLink}>{g.title}</Link>
                </h3>
                <p style={{ ...styles.cardDesc, margin: 0 }}>{g.desc}</p>
                <Link to={g.url} style={{ ...styles.cardArrow, textDecoration: 'none' }} aria-hidden="true" tabIndex={-1}>Read guide →</Link>
              </article>
            ))}
          </div>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>{state.name} CE — quick answers</h2>
          {hub.faq.map(f => (
            <div key={f.q} style={styles.faqItem}>
              <h3 style={styles.faqQ}>{f.q}</h3>
              <p style={styles.faqA}>{f.a}</p>
              <Link to={f.link.url} style={styles.faqLink}>{f.link.label} →</Link>
            </div>
          ))}
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>Official {state.regulator} resources</h2>
          <ul style={styles.sources}>
            {hub.sources.map(s => (
              <li key={s.url} style={styles.sourceItem}>
                <a href={s.url} target="_blank" rel="noopener noreferrer" style={styles.sourceLink}>{s.label}</a>
                <span style={styles.sourceDesc}> — {s.desc}</span>
              </li>
            ))}
          </ul>
        </section>

        <div style={styles.ctaSection}>
          <div style={styles.ctaInner}>
            <div>
              <div style={styles.ctaLabel}>Ready to renew?</div>
              <div style={styles.ctaTitle}>Complete {state.name} CE Package — ${state.packagePrice}</div>
              <div style={styles.ctaSub}>{hub.ctaSub}</div>
            </div>
            <Link to={`/${state.slug}/courses`} style={styles.ctaBtn}>See {state.name} courses →</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  crumbs: { fontSize: 13, color: 'rgba(239,230,214,0.7)', marginBottom: 14 },
  crumbLink: { color: 'rgba(239,230,214,0.85)', textDecoration: 'none' },
  reviewed: { fontSize: 13, color: 'rgba(239,230,214,0.7)', marginTop: 14, marginBottom: 0 },
  section: { marginBottom: 44 },
  h2: { fontFamily: 'var(--font-display)', fontSize: 26, color: '#243A34', marginBottom: 18 },
  facts: { background: 'white', border: '1px solid #e8e2d6', borderRadius: 14, margin: 0, overflow: 'hidden' },
  factRow: { display: 'flex', flexWrap: 'wrap', gap: '4px 24px', padding: '14px 22px', borderBottom: '1px solid #f0ebe1' },
  factTerm: { flex: '0 0 190px', fontWeight: 700, fontSize: 14, color: '#2C453E' },
  factValue: { flex: '1 1 260px', margin: 0, fontSize: 15, color: 'var(--slate-700, #334155)', lineHeight: 1.55 },
  cardTitleLink: { color: '#243A34', textDecoration: 'none' },
  faqItem: { background: 'white', border: '1px solid #e8e2d6', borderRadius: 14, padding: '20px 24px', marginBottom: 14 },
  faqQ: { fontSize: 17, fontWeight: 700, color: '#243A34', margin: '0 0 8px' },
  faqA: { fontSize: 15, lineHeight: 1.65, color: 'var(--slate-700, #334155)', margin: '0 0 10px' },
  faqLink: { fontSize: 14, fontWeight: 700, color: '#2C453E' },
  sources: { listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 10 },
  sourceItem: { fontSize: 15, lineHeight: 1.55 },
  sourceLink: { fontWeight: 700, color: '#2C453E' },
  sourceDesc: { color: 'var(--color-text-muted)' },
  page:{ background: '#FAF7F2', minHeight: '100vh' },
  hero: { background: 'linear-gradient(135deg, #2C453E 0%, #243A34 100%)', color: '#EFE6D6', padding: '48px 0 40px' },
  heroTitle: { fontFamily: 'var(--font-display)', fontSize: 40, lineHeight: 1.15, color: 'white', marginBottom: 12 },
  heroSubtitle: { fontSize: 17, lineHeight: 1.6, maxWidth: 720, color: 'rgba(239,230,214,0.85)' },
  content: { paddingTop: 40, paddingBottom: 60 }, // vertical only, so .container keeps its side gutter on phones
  emptyState: { textAlign: 'center', padding: '60px 20px', background: 'white', border: '1px solid #e8e2d6', borderRadius: 14 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 320px), 1fr))', gap: 20, marginBottom: 40 },
  card: { background: 'white', border: '1px solid #e8e2d6', borderRadius: 14, padding: 24, textDecoration: 'none', color: '#243A34', display: 'flex', flexDirection: 'column', gap: 10 },
  cardLabel: { display: 'inline-block', alignSelf: 'flex-start', fontSize: 11, fontWeight: 700, color: '#2C453E', background: '#EFE6D6', padding: '3px 10px', borderRadius: 999, letterSpacing: '0.02em', textTransform: 'uppercase' },
  cardTitle: { fontFamily: 'var(--font-display)', fontSize: 18, lineHeight: 1.3, color: '#243A34', marginTop: 4 },
  cardDesc: { fontSize: 14, color: 'var(--color-text-muted)', lineHeight: 1.55, flex: 1 },
  cardArrow: { fontSize: 13, fontWeight: 700, color: '#2C453E', marginTop: 6 },
  ctaSection: { background: 'linear-gradient(135deg, #2C453E, #243A34)', borderRadius: 16, padding: 32, color: 'white' },
  ctaInner: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap' },
  ctaLabel: { fontSize: 12, color: '#B6A189', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 6 },
  ctaTitle: { fontFamily: 'var(--font-display)', fontSize: 22, marginBottom: 6 },
  ctaSub: { fontSize: 14, color: 'rgba(255,255,255,0.75)', maxWidth: 540 },
  ctaBtn: { display: 'inline-block', background: '#B6A189', color: 'white', textDecoration: 'none', fontWeight: 700, padding: '14px 28px', borderRadius: 10, fontSize: 15, whiteSpace: 'nowrap' },
};
